import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertCircle, FiShield, FiClock, FiFileText, FiDollarSign, FiTrendingUp } = FiIcons;

const UseCases = () => {
  const useCases = [
    {
      category: 'FinTech & Payment Services',
      urgency: 'Immediate Compliance Required',
      color: 'from-red-600 to-orange-600',
      requirements: [
        'Payment Service Directive 2 (PSD2) compliance for EU operations',
        'Money Service Business (MSB) registration and ongoing obligations',
        'Customer Due Diligence (CDD) and Enhanced Due Diligence (EDD) procedures',
        'Transaction monitoring for amounts exceeding €1,000',
        'Suspicious Activity Reporting (SAR) within strict timeframes'
      ],
      penalties: 'Up to 10% of annual turnover or €5M (whichever is higher)',
      timeline: '6 months from license approval'
    },
    {
      category: 'Real Estate & Property',
      urgency: 'High-Risk Sector Scrutiny',
      color: 'from-orange-600 to-amber-600',
      requirements: [
        'Customer identification for transactions exceeding €10,000',
        'Beneficial ownership disclosure for corporate buyers',
        'Source of funds verification for high-value transactions',
        'Ongoing monitoring of business relationships',
        'Record keeping for minimum 5 years post-transaction'
      ],
      penalties: 'Criminal sanctions and unlimited fines',
      timeline: 'Immediate compliance upon transaction threshold'
    },
    {
      category: 'Professional Services',
      urgency: 'Expanding Regulatory Scope',
      color: 'from-amber-600 to-yellow-600',
      requirements: [
        'Trust and Company Service Provider (TCSP) obligations',
        'Customer risk assessment and ongoing monitoring',
        'Politically Exposed Persons (PEP) screening procedures',
        'Training programs for relevant employees',
        'Annual compliance reporting to supervisory authorities'
      ],
      penalties: 'Professional license suspension or revocation',
      timeline: 'Within 3 months of regulatory designation'
    },
    {
      category: 'Gaming & Entertainment',
      urgency: 'Emerging Compliance Area',
      color: 'from-purple-600 to-pink-600',
      requirements: [
        'Player verification and age confirmation procedures',
        'Large transaction reporting for amounts exceeding €2,000',
        'Source of wealth verification for high-value players',
        'Suspicious betting pattern identification and reporting',
        'Cross-border transaction monitoring and reporting'
      ],
      penalties: 'License revocation and criminal prosecution',
      timeline: 'Compliance required before operational license'
    }
  ];

  return (
    <section id="use-cases" className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50 relative overflow-hidden">
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200 rounded-full blur-3xl opacity-30"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
            <span>Urgent Compliance Requirements</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Real Compliance Scenarios
            <br />
            <span className="text-emerald-700">Requiring Immediate Action</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            These are actual regulatory requirements that companies face today. 
            Failure to comply results in severe penalties and operational disruption.
          </p>
        </motion.div>

        <div className="space-y-8">
          {useCases.map((useCase, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="bg-white rounded-3xl shadow-xl border-2 border-gray-200 overflow-hidden hover:border-emerald-500 transition-all duration-300"
            >
              <div className={`bg-gradient-to-r ${useCase.color} px-8 py-6`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {useCase.category}
                    </h3>
                    <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                      <SafeIcon icon={FiClock} className="w-4 h-4 text-white" />
                      <span className="text-white text-sm font-medium">{useCase.urgency}</span>
                    </div>
                  </div>
                  <div className="mt-4 md:mt-0 bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4">
                    <div className="text-white/80 text-sm mb-1">Compliance Timeline</div>
                    <div className="text-xl font-bold text-white">{useCase.timeline}</div>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h4 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2">
                      <SafeIcon icon={FiFileText} className="w-5 h-5 text-emerald-600" />
                      <span>Legal Requirements</span>
                    </h4>
                    <ul className="space-y-4">
                      {useCase.requirements.map((requirement, reqIndex) => (
                        <li key={reqIndex} className="flex items-start space-x-3 bg-emerald-50 rounded-xl p-4">
                          <SafeIcon icon={FiShield} className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-6 flex items-center space-x-2">
                      <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-red-600" />
                      <span>Non-Compliance Penalties</span>
                    </h4>
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-2xl p-6">
                      <p className="text-red-800 font-semibold leading-relaxed">{useCase.penalties}</p>
                    </div>
                    
                    <button 
                      onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                      className="w-full mt-6 bg-gradient-to-r from-emerald-600 to-green-700 text-white px-6 py-4 rounded-xl hover:from-emerald-700 hover:to-green-800 transition-all duration-300 font-semibold shadow-lg"
                    >
                      Get Compliant Now
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 bg-gradient-to-r from-gray-900 to-emerald-900 rounded-3xl p-10 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMTBiOTgxIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          
          <div className="relative">
            <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Regulatory Enforcement is Increasing
            </h3>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-8 leading-relaxed">
              Authorities are expanding AML/CFT requirements to more sectors and increasing penalty enforcement. 
              Companies that act now avoid the rush and ensure proper implementation before regulatory scrutiny intensifies.
            </p>
            <button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-emerald-600 to-green-700 text-white px-10 py-5 rounded-xl hover:from-emerald-700 hover:to-green-800 transition-all duration-300 font-semibold text-lg shadow-2xl inline-flex items-center space-x-3"
            >
              <span>Schedule Compliance Assessment</span>
              <SafeIcon icon={FiShield} className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UseCases;